import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Select, TextField, SortMenu, SelectToolbar, Button } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  DropdownWrapper,
  SelectToolbarWrapper,
  ClearFiltersButtonWrapper,
} from "./Filters.styled";
import  { AddUsersToGroupsModal, AddUsersToRolesModal, DisableUsersModal } from "../../components";
import { getRoleList, getGroupList, getUserStatusList } from "../../../../services";
import { formatStatusLabel } from "../../../../../../../utils/uiUtils";

const Filters = ({
  selectedItemsCount = 0,
  selectedUserUuids = [],
  onClearSelection,
  onSortChange,
  onOrderChange,
  selectedSortOption = "createdAt",
  orderDirection = "desc",
  onSearchChange,
  onRoleChange,
  onGroupChange,
  onStatusChange,
  loading = false,
  onRefetchUsers,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedRole, setSelectedRole] = useState(undefined);
  const [selectedGroup, setSelectedGroup] = useState(undefined);
  const [selectedStatus, setSelectedStatus] = useState(undefined);
  const [isAddUsersToGroupsModalOpen, setIsAddUsersToGroupsModalOpen] = useState(false);
  const [isAddUsersToRolesModalOpen, setIsAddUsersToRolesModalOpen] = useState(false);
  const [isDisableUsersModalOpen, setIsDisableUsersModalOpen] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  const searchTimeoutRef = useRef(null);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for 500ms debounce
    searchTimeoutRef.current = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(value);
      }
    }, 500);
  }, [onSearchChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRoleChange = (value) => {
    // Extract value if it's an object, otherwise use the value directly
    const roleValue = typeof value === 'object' && value !== null ? value.value : value;
    setSelectedRole(roleValue);
    if (onRoleChange) {
      onRoleChange(roleValue);
    }
  };

  const handleGroupChange = (value) => {
    // Extract value if it's an object, otherwise use the value directly
    const groupValue = typeof value === 'object' && value !== null ? value.value : value;
    setSelectedGroup(groupValue);
    if (onGroupChange) {
      onGroupChange(groupValue);
    }
  };

  const handleStatusChange = (value) => {
    // Extract value if it's an object, otherwise use the value directly
    const statusValue = typeof value === 'object' && value !== null ? value.value : value;
    setSelectedStatus(statusValue);
    if (onStatusChange) {
      // Convert status to uppercase for API (e.g., "active" -> "ACTIVE")
      const statusUpper = statusValue ? statusValue.toUpperCase() : null;
      onStatusChange(statusUpper);
    }
  };

  const handleClearFilters = () => {
    // Clear search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Call all onChange callbacks FIRST to update parent state and trigger API call
    // This ensures the parent component receives the empty values immediately
    if (onSearchChange) {
      onSearchChange("");
    }

    if (onRoleChange) {
      onRoleChange(null);
    }

    if (onGroupChange) {
      onGroupChange(null);
    }

    if (onStatusChange) {
      onStatusChange(null);
    }

    // Then reset local state
    setSearchValue("");
    setSelectedRole(undefined);
    setSelectedGroup(undefined);
    setSelectedStatus(undefined);
    
    // Increment keys to force re-render of components
    setFiltersKey((prev) => prev + 1);
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(
      searchValue ||
      selectedRole ||
      selectedGroup ||
      selectedStatus
    );
  }, [searchValue, selectedRole, selectedGroup, selectedStatus]);

  // Get selected role object with value and label
  const selectedRoleValue = useMemo(() => {
    if (!selectedRole) return undefined;
    const role = roleOptions.find((r) => r.value === selectedRole);
    return role ? { value: role.value, label: role.label } : undefined;
  }, [selectedRole, roleOptions]);

  // Get selected group object with value and label
  const selectedGroupValue = useMemo(() => {
    if (!selectedGroup) return undefined;
    const group = groupOptions.find((g) => g.value === selectedGroup);
    return group ? { value: group.value, label: group.label } : undefined;
  }, [selectedGroup, groupOptions]);

  // Get selected status object with value and label
  const selectedStatusValue = useMemo(() => {
    if (!selectedStatus) return undefined;
    const status = statusOptions.find((s) => s.value === selectedStatus);
    return status ? { value: status.value, label: status.label } : undefined;
  }, [selectedStatus, statusOptions]);

  // Fetch roles from API
  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await getRoleList({
          page: 1,
          page_size: 100, // Get all roles
          order_by: ["name"],
          order_direction: ["ASC"],
        });

        if (response && response.body) {
          const mappedRoles = response.body.map((role) => ({
            value: role.roleUuid,
            label: role.name,
          }));
          setRoleOptions(mappedRoles);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  // Fetch groups from API
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await getGroupList({
          page: 1,
          page_size: 100, // Get all groups
          order_by: ["name"],
          order_direction: ["ASC"],
        });

        if (response && response.body) {
          const mappedGroups = response.body.map((group) => ({
            value: group.uuid || group.groupUuid, // API returns 'uuid', fallback to 'groupUuid' for compatibility
            label: group.name,
          }));
          setGroupOptions(mappedGroups);
        }
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchGroups();
  }, []);

  // Fetch statuses from API
  useEffect(() => {
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await getUserStatusList({
          page: 1,
          page_size: 100, // Get all statuses
          order_by: ["status"],
          order_direction: ["ASC"],
        });

        if (response && response.body) {
          const mappedStatuses = response.body.map((statusItem) => ({
            value: statusItem.status,
            label: formatStatusLabel(statusItem.status),
          }));
          setStatusOptions(mappedStatuses);
        }
      } catch (error) {
        console.error("Error fetching statuses:", error);
      } finally {
        setLoadingStatuses(false);
      }
    };

    fetchStatuses();
  }, []);

  const isMultipleUsers = selectedItemsCount > 1;

  if (selectedItemsCount >= 1) {
    return (
      <>
        <SelectToolbarWrapper firstThreeIcons={selectedItemsCount > 1}>
          <SelectToolbar 
            items={selectedItemsCount} 
            itemLabel='User'
            onClose={() => {
              if (onClearSelection) {
                onClearSelection();
              }
            }}
          >
            <SelectToolbar.Action
              icon='lock_open'
              tooltip={selectedItemsCount > 1 ? 'Assign roles to selected users' : 'Assign a role to selected user'}
              onClick={isMultipleUsers ? undefined : () => setIsAddUsersToRolesModalOpen(true)}
            />
            <SelectToolbar.Action
              icon='group_add'
              tooltip={selectedItemsCount > 1 ? 'Add selected users to group' : 'Add selected user to group'}
              onClick={isMultipleUsers ? undefined : () => {
                // When there's exactly 1 user selected, open modal with the userUuid
                if (selectedUserUuids.length === 1) {
                  setIsAddUsersToGroupsModalOpen(true);
                }
              }}
            />
            <SelectToolbar.Action
              icon='group_remove'
              tooltip={selectedItemsCount > 1 ? 'Remove selected users from group' : 'Remove selected user from group'}
              onClick={undefined}
            />
            <SelectToolbar.Action
              icon='person_off'
              tooltip={selectedItemsCount > 1 ? 'Disable users' : 'Disable user'}
              onClick={() => setIsDisableUsersModalOpen(true)}
            />
          </SelectToolbar>
        </SelectToolbarWrapper>

        <AddUsersToGroupsModal
          isOpen={isAddUsersToGroupsModalOpen}
          selectedUsers={selectedUserUuids.map((userUuid) => ({ id: userUuid, userUuid }))}
          onClose={() => setIsAddUsersToGroupsModalOpen(false)}
        />

        <AddUsersToRolesModal
            isOpen={isAddUsersToRolesModalOpen}
            selectedUsers={selectedUserUuids.map((userUuid) => ({ id: userUuid, userUuid }))}
            onClose={() => setIsAddUsersToRolesModalOpen(false)}
        />

        <DisableUsersModal
          isOpen={isDisableUsersModalOpen}
          selectedUsers={selectedUserUuids.map((userUuid) => ({ id: userUuid, userUuid }))}
          onClose={() => setIsDisableUsersModalOpen(false)}
          onRefetchUsers={onRefetchUsers}
        />

      </>
    );
  }

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <TextField
          iconLeft="search"
          onChange={handleSearchChange}
          size="small"
          placeholder="Search"
          value={searchValue}
          disabled={loading}
        />

        <DropdownWrapper>
          <Select
            key={`role-filter-${filtersKey}`}
            label="Role"
            placeholder={loadingRoles ? "Loading roles..." : "Role"}
            onChange={handleRoleChange}
            size="small"
            value={selectedRoleValue}
            width="auto"
            minWidth="60px"
            disabled={loading || loadingRoles}
          >
            <Select.Menu>
              {roleOptions.map((role) => (
                <Select.Item key={role.value} value={role.value} label={role.label}>
                  {role.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
        </DropdownWrapper>

        <DropdownWrapper>
          <Select
            key={`group-filter-${filtersKey}`}
            label="Group"
            placeholder={loadingGroups ? "Loading groups..." : "Group"}
            onChange={handleGroupChange}
            size="small"
            value={selectedGroupValue}
            width="auto"
            minWidth="60px"
            disabled={loading || loadingGroups}
          >
            <Select.Menu>
              {groupOptions.map((group) => (
                <Select.Item key={group.value} value={group.value} label={group.label}>
                  {group.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
        </DropdownWrapper>

        <DropdownWrapper>
          <Select
            key={`status-filter-${filtersKey}`}
            label="Status"
            placeholder={loadingStatuses ? "Loading statuses..." : "Status"}
            onChange={handleStatusChange}
            size="small"
            value={selectedStatusValue}
            width="auto"
            minWidth="60px"
            disabled={loading || loadingStatuses}
          >
            <Select.Menu>
              {statusOptions.map((status) => (
                <Select.Item key={status.value} value={status.value} label={status.label}>
                  {status.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
        </DropdownWrapper>

        {hasActiveFilters && (
          <ClearFiltersButtonWrapper>
            <Button
              label="Clear Filters"
              size="medium"
              type="secondary"
              onClick={handleClearFilters}
            />
          </ClearFiltersButtonWrapper>
        )}
      </FiltersLeftContent>

      <FiltersRightContent>
        <SortMenu
          minWidth="200px"
          onChange={(id) => {
            if (onSortChange) {
              onSortChange(id);
            }
          }}
          order={orderDirection ? orderDirection.toLowerCase() : "desc"}
          onOrderChange={(newOrder) => {
            if (onOrderChange) {
              onOrderChange(newOrder);
            }
          }}
          options={[
            {
              icon: 'clock',
              id: 'createdAt',
              label: 'Most Recent'
            },
            {
              icon: 'sortByAlpha',
              id: 'fullName',
              label: 'Name (A-Z)'
            },
            {
              icon: 'email',
              id: 'email',
              label: 'Email'
            },
            {
              icon: 'warning',
              id: 'status',
              label: 'Priority'
            },
            {
              icon: 'update',
              id: 'updatedAt',
              label: 'Last Updated'
            }
          ]}
          selected={selectedSortOption || "createdAt"}
          size="medium"
          title="SORT BY"
          width="240px"
          disabled={loading}
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;

